import type { QueryClient } from "@tanstack/react-query";
import { blogPosts } from "@/content/blogPosts";
import { SEO_DEFAULT_DESCRIPTION, SEO_DEFAULT_IMAGE, SEO_SITE_NAME, STATIC_SEO } from "@shared/seo";

export type HeadMeta = {
  title: string;
  description: string;
  ogType?: "website" | "article";
  ogImage?: string;
  ogImageWidth?: number;
  ogImageHeight?: number;
  ogImageAlt?: string;
  publishedTime?: string;
  modifiedTime?: string;
  canonicalPath?: string;
  noindex?: boolean;
  notFound?: boolean;
};

export type SsrPrefetch = Record<string, never>;

export function resolveHeadMeta(url: string): HeadMeta {
  let pathOnly = url.split("?")[0] || "/";
  try { pathOnly = decodeURI(pathOnly); } catch { /* mirror wouter's fail-safe behavior */ }
  const clean = pathOnly.replace(/\/+$/, "") || "/";
  const lower = clean.toLowerCase();

  const staticEntry = STATIC_SEO[lower];
  if (staticEntry) return { ...staticEntry, ogType: "website" };

  const articleMatch = lower.match(/^\/blog\/([^/]+)$/);
  if (articleMatch) {
    const post = blogPosts.find((item) => item.slug === articleMatch[1]);
    if (!post) {
      return { title: `Page Not Found | ${SEO_SITE_NAME}`, description: SEO_DEFAULT_DESCRIPTION, notFound: true, noindex: true };
    }
    return {
      title: `${post.title} | ${SEO_SITE_NAME}`,
      description: post.excerpt,
      ogType: "article",
      ogImage: SEO_DEFAULT_IMAGE,
      ogImageWidth: 1400,
      ogImageHeight: 1100,
      ogImageAlt: "A physical therapist guiding shoulder movement with an active woman over 50 during a one-on-one session",
      publishedTime: `${post.published}T12:00:00.000Z`,
      canonicalPath: `/blog/${post.slug}`,
    };
  }

  return { title: `Page Not Found | ${SEO_SITE_NAME}`, description: SEO_DEFAULT_DESCRIPTION, notFound: true, noindex: true };
}

export async function prefetchForPath(url: string, _queryClient: QueryClient, _prefetch: SsrPrefetch): Promise<HeadMeta> {
  return resolveHeadMeta(url);
}
